import { socketManager } from "@/services/websocket/socket-manager";
import { selectUser } from "@/store/slices/user.slice";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { initSessionListeners } from "./listeners/user-session";
import { useLogout } from "@/hooks/useLogout";
import { initNotificationListeners } from "./listeners/notification";
import { initChatListeners } from "./listeners/chat";
import { chatHub } from "./chat-hub";
import { SOCKET_NAME_SPACES } from "./socket.enum";

// Hook being used to initialize socket only if auth success

export const useInitSocket = () => {
  const authUser = useSelector(selectUser).user;
  const { logoutUser } = useLogout();

  useEffect(() => {
    if (!authUser) return;

    const tokens = getAuthTokensFromLocalStorage();
    if (!tokens?.accessToken || !tokens?.deviceId) return;

    // Auth provider — read the token FRESH on every (re)connect so reconnects
    // after a silent refresh use the rotated token, not a stale snapshot.
    const authProvider = (cb: (data: Record<string, unknown>) => void) => {
      const latest = getAuthTokensFromLocalStorage();
      cb({ accessToken: latest?.accessToken ?? "" });
    };

    // 1️⃣ Session socket
    socketManager.connect(SOCKET_NAME_SPACES.SESSIONS, {
      query: {
        userId: authUser.id,
        deviceId: tokens.deviceId,
      },
      auth: authProvider,
    });

    // 2️⃣ Listen session event and chain notifications only if session connected.
    // `onConnect`'s callback fires on every reconnect (not just the first
    // connect), but initSessionListeners/initNotificationListeners attach
    // fresh closures each call — so without these guards, every reconnect
    // piles on duplicate listeners (duplicate toasts/sounds on each retry).
    let sessionListenersInitialized = false;
    socketManager.onConnect(SOCKET_NAME_SPACES.SESSIONS, () => {
      if (sessionListenersInitialized) return;
      sessionListenersInitialized = true;

      initSessionListeners(logoutUser);

      socketManager.connect(SOCKET_NAME_SPACES.NOTIFICATIONS, {
        auth: authProvider,
      });

      // Listen notification event only if notificaiton connected
      let notificationListenersInitialized = false;
      socketManager.onConnect(SOCKET_NAME_SPACES.NOTIFICATIONS, () => {
        if (notificationListenersInitialized) return;
        notificationListenersInitialized = true;
        initNotificationListeners();
      });

      // Chat: presence ("online") means the app is open in some tab.
      socketManager.connect(SOCKET_NAME_SPACES.CHAT, { auth: authProvider });
      let chatListenersInitialized = false;
      socketManager.onConnect(SOCKET_NAME_SPACES.CHAT, () => {
        if (!chatListenersInitialized) {
          chatListenersInitialized = true;
          initChatListeners();
        }
        // Every (re)connect: catch up, renew presence subscriptions.
        chatHub.onConnected();
      });
    });

    return () => {
      socketManager.disconnectAll();
      chatHub.reset();
    };
  }, [authUser]);
};
